import axios from "axios"
import type { QueryClient } from "@tanstack/react-query"
import api from "@/lib/api"
import { depoOku, depoSil, depoYaz } from "./depo"
import { uploadFile } from "@/lib/files"
import {
  type DenetimEkibi, type DosyaFormu, type ElevatorProject, type HariciDokuman,
  denetimEkibiAnahtari, dosyaFormunuYaz, hariciDokumanAnahtari, updateProjectValues,
} from "@/lib/elevator-projects"
import type { ContentTypeNote } from "@/lib/content-type-collab"
import type {
  ElevatorChecklistSession, ItemResponse, PaginatedSessions, SessionDetail, UpsertResponsePayload,
} from "@/lib/checklist-entries"

/**
 * ÇEVRİMDIŞI KOMUT KUYRUĞU — faaliyet dosyasında bağlantı yokken yapılan işler.
 *
 * Her işlem bir KOMUT: kimliği istemcide üretilir ve sunucuya
 * `Idempotency-Key` olarak gider. Bağlantı yanıt gelmeden koparsa komut
 * AYNI kimlikle yeniden gönderilir; sunucu işi ikinci kez yapmaz, ilk yanıtı
 * döndürür (bkz. backend `TekrarGuvenliInterceptor`). Böylece süreç başlat,
 * tamamla, çoğalt gibi tekrarlanamaz işler de güvenle kuyruğa alınır.
 *
 * SIRA korunur: kuyrukta bekleyen komut varken yeni komut da doğrudan
 * gönderilmez, kuyruğun sonuna girer — "başlat"tan önce "yaz" gitmesin.
 *
 * Aynı hedefe art arda yapılan ÜZERİNE YAZMALAR (form değerleri, madde yanıtı,
 * Genel Bilgiler alanları) tek komutta birleşir; eski komutun YERİNDE kalır
 * (sıra bozulmaz) ama yeni kimlik alır — içerik değişti, eski kimliğin
 * saklanmış yanıtı yeni içeriği temsil etmez.
 *
 * Kuyruğa giren her komut ekranların okuduğu önbelleğe de işlenir: bağlantı
 * yokken sayfa yenilense de ekran cihazdaki son hâli gösterir.
 *
 * Sunucunun REDDETTİĞİ komut (4xx — ör. "form zaten tamamlandı", "kopya
 * numarası dolu") kuyruktan çıkar ve ÇAKIŞMA LİSTESİNE yazılır; kullanıcı
 * neyin uygulanmadığını oradan görür.
 */

export type KomutTuru =
  | "form.yaz" | "form.baslat" | "form.tamamla" | "form.cogalt"
  | "genel.yaz"
  | "oturum.ac" | "yanit.yaz" | "yanit.sil" | "oturum.tamamla"
  | "dosya.yukle"
  | "not.ekle" | "not.yaz" | "not.sil" | "not.sirala"
  | "ekip.uye.ekle" | "ekip.uye.durum" | "ekip.uye.cikar" | "ekip.birim.ekle" | "ekip.birim.sil"

interface Istek {
  method: "post" | "put" | "patch" | "delete"
  url: string
  body?: unknown
}

export interface Komut {
  /** İstemcide üretilen kimlik = Idempotency-Key. */
  id: string
  tur: KomutTuru
  projectId: string
  /** Çakışma listesinde görünen kısa açıklama. */
  ozet: string
  zaman: number
  /** Aynı hedefe üzerine yazan komutların ortak anahtarı (birleştirme). */
  birlestir?: string
  istek?: Istek
  /** `dosya.yukle`: bayt cihazda (`depo`), bağlantı gelince yüklenir. */
  dosya?: { blobAnahtari: string; ad: string; mime: string; boyut: number; auditSetItemId: string; kopyaNo: number }
  /** Önbelleğe işlemek için ek bilgi (gönderilmez). */
  ek?: Record<string, unknown>
}

export interface Reddedilen {
  id: string
  ozet: string
  hata: string
  zaman: number
  projectId: string
}

export interface KuyrukDurumu {
  bekleyen: number
  gonderiliyor: boolean
  /** Sunucunun reddettiği ve kuyruktan çıkan komutlar (çakışma listesi). */
  reddedilen: number
}

const DEPO_ANAHTARI = "kuyruk"
const RED_ANAHTARI = "kuyruk-reddedilenler"
let komutlar: Komut[] = []
let reddedilenler: Reddedilen[] = []
let yuklendi: Promise<void> | null = null
let gonderiliyor = false
let durum: KuyrukDurumu = { bekleyen: 0, gonderiliyor: false, reddedilen: 0 }
const dinleyiciler = new Set<() => void>()
let qcRef: QueryClient | null = null

/** RFC 4122 v4 — `crypto.randomUUID` güvenli bağlam ister; yoksa yedek. */
export function yeniKimlik(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("")
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

function bildir() {
  durum = { bekleyen: komutlar.length, gonderiliyor, reddedilen: reddedilenler.length }
  dinleyiciler.forEach((d) => d())
}

/* --- Eski biçim (ilk sürümün kayıtları) — sahada bekleyen veri kaybolmasın --- */
type EskiKayit =
  | { tur: "form"; projectId: string; zaman: number; govde: Parameters<typeof dosyaFormunuYaz>[1] }
  | { tur: "genel"; projectId: string; zaman: number; degerler: Record<string, unknown> }
  | { tur: "yanit"; projectId: string; zaman: number; checklistId: string; sessionId: string | null; govde: UpsertResponsePayload }
  | { tur: "yanit-sil"; projectId: string; zaman: number; checklistId: string; sessionId: string | null; checklistItemId: string }

function eskidenCevir(k: EskiKayit | Komut): Komut | null {
  if ("id" in k && "ozet" in k) return k
  const e = k as EskiKayit
  switch (e.tur) {
    case "form": return formYazKomutu(e.projectId, e.govde)
    case "genel": return genelKomutu(e.projectId, e.degerler)
    // Oturumu bilinmeyen eski yanıt gönderilemez (oturum artık istemcide açılıyor).
    case "yanit": return e.sessionId ? yanitKomutu(e.projectId, e.sessionId, e.govde) : null
    case "yanit-sil": return e.sessionId ? yanitSilKomutu(e.projectId, e.sessionId, e.checklistItemId) : null
    default: return null
  }
}

function yukle() {
  if (!yuklendi) {
    yuklendi = Promise.all([depoOku<(Komut | EskiKayit)[]>(DEPO_ANAHTARI), depoOku<Reddedilen[]>(RED_ANAHTARI)]).then(([k, r]) => {
      const okunan = (Array.isArray(k) ? k : []).map(eskidenCevir).filter((x): x is Komut => !!x)
      // Yükleme sürerken eklenenler de kalsın.
      komutlar = [...okunan, ...komutlar]
      reddedilenler = [...(Array.isArray(r) ? r : []), ...reddedilenler]
      bildir()
    })
  }
  return yuklendi
}

async function sakla() {
  await Promise.all([depoYaz(DEPO_ANAHTARI, komutlar), depoYaz(RED_ANAHTARI, reddedilenler)])
  bildir()
}

/** Kuyruğa ekler; birleşen üzerine yazma eski komutun YERİNİ alır (yeni kimlikle). */
async function ekle(k: Komut) {
  await yukle()
  const i = k.birlestir ? komutlar.findIndex((x) => x.birlestir === k.birlestir) : -1
  if (i >= 0) {
    const eski = komutlar[i]
    const yeni: Komut =
      k.tur === "genel.yaz" && eski.tur === "genel.yaz"
        ? {
            ...k,
            istek: {
              ...k.istek!,
              body: { values: { ...(eski.istek!.body as { values: object }).values, ...(k.istek!.body as { values: object }).values } },
            },
          }
        : k
    komutlar = [...komutlar.slice(0, i), yeni, ...komutlar.slice(i + 1)]
  } else {
    komutlar = [...komutlar, k]
  }
  await sakla()
}

/** Yanıt yok = istek sunucuya ulaşmadı (bağlantı yok, sunucu kapalı). */
export function agHatasiMi(e: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true
  return axios.isAxiosError(e) && !e.response
}

export const kuyrukDurumu = (): KuyrukDurumu => durum
export const reddedilenListesi = (): Reddedilen[] => reddedilenler
export function kuyruguDinle(dinleyici: () => void) {
  dinleyiciler.add(dinleyici)
  return () => { dinleyiciler.delete(dinleyici) }
}

/** Çakışma listesini temizler (kullanıcı gördü). */
export async function reddedilenleriTemizle() {
  await yukle()
  reddedilenler = []
  await sakla()
}

// --- Komut kurucuları ----------------------------------------------------------

const dosyaYolu = (projectId: string) => `/elevator-projects/${projectId}`

function formYazKomutu(projectId: string, govde: Parameters<typeof dosyaFormunuYaz>[1]): Komut {
  return {
    id: yeniKimlik(), tur: "form.yaz", projectId, zaman: Date.now(), ozet: "Form değerleri",
    birlestir: `form:${projectId}:${govde.auditSetItemId}:${govde.kopyaNo}`,
    istek: { method: "put", url: `${dosyaYolu(projectId)}/forms`, body: govde },
  }
}
function genelKomutu(projectId: string, degerler: Record<string, unknown>): Komut {
  return {
    id: yeniKimlik(), tur: "genel.yaz", projectId, zaman: Date.now(), ozet: "Genel Bilgiler",
    birlestir: `genel:${projectId}`,
    istek: { method: "patch", url: `${dosyaYolu(projectId)}/values`, body: { values: degerler } },
  }
}
function yanitKomutu(projectId: string, sessionId: string, govde: UpsertResponsePayload): Komut {
  return {
    id: yeniKimlik(), tur: "yanit.yaz", projectId, zaman: Date.now(), ozet: "Kontrol listesi yanıtı",
    birlestir: `yanit:${sessionId}:${govde.checklistItemId}`,
    istek: { method: "post", url: `${dosyaYolu(projectId)}/checklist-entries/${sessionId}/responses`, body: govde },
    ek: { sessionId },
  }
}
function yanitSilKomutu(projectId: string, sessionId: string, checklistItemId: string): Komut {
  return {
    id: yeniKimlik(), tur: "yanit.sil", projectId, zaman: Date.now(), ozet: "Kontrol listesi yanıtı (silme)",
    birlestir: `yanit:${sessionId}:${checklistItemId}`,
    istek: { method: "delete", url: `${dosyaYolu(projectId)}/checklist-entries/${sessionId}/responses/${checklistItemId}` },
    ek: { sessionId, checklistItemId },
  }
}

// --- Çalıştırma ----------------------------------------------------------------

export type Sonuc<T> = { kuyrukta: true } | { kuyrukta: false; veri: T }

/** Yeniden denemenin anlamı olmayan istemci hatası. */
class KaliciHata extends Error {}

/**
 * Komutu çalıştırır: kuyrukta bekleyen yoksa hemen gönderir; bağlantı
 * yoksa (ya da sırayı korumak için) kuyruğa alır ve önbelleğe işler.
 * Sunucunun reddi (4xx) çağırana hata olarak döner — bağlantı varken
 * kullanıcı hatayı hemen görmeli.
 */
async function calistir<T>(k: Komut): Promise<Sonuc<T>> {
  await yukle()
  if (komutlar.length === 0 && !(typeof navigator !== "undefined" && navigator.onLine === false)) {
    try {
      return { kuyrukta: false, veri: (await gonderBir(k)) as T }
    } catch (e) {
      if (!agHatasiMi(e)) throw e
    }
  }
  await ekle(k)
  onbellegeIsle(k)
  void kuyruguGonder()
  return { kuyrukta: true }
}

async function gonderBir(k: Komut): Promise<unknown> {
  const basliklar = { "Idempotency-Key": k.id }
  if (k.tur === "dosya.yukle" && k.dosya) {
    const blob = await depoOku<Blob>(k.dosya.blobAnahtari)
    if (!blob) throw new KaliciHata("Cihazdaki dosya bulunamadı")
    const fileId = await uploadFile(new File([blob], k.dosya.ad, { type: k.dosya.mime }))
    const { data } = await api.post(
      `${dosyaYolu(k.projectId)}/forms/files`,
      { auditSetItemId: k.dosya.auditSetItemId, kopyaNo: k.dosya.kopyaNo, fileId },
      { headers: basliklar },
    )
    await depoSil(k.dosya.blobAnahtari)
    return data
  }
  const { method, url, body } = k.istek!
  const { data } = await api.request({ method, url, data: body, headers: basliklar })
  return data
}

// --- Dışarıya açılan işlemler (ekranlar bunları kullanır) ---------------------

export type YazmaSonucu = { kuyrukta: boolean }

export async function formuYaz(projectId: string, govde: Parameters<typeof dosyaFormunuYaz>[1]): Promise<YazmaSonucu> {
  return calistir(formYazKomutu(projectId, govde))
}

export async function formuBaslat(projectId: string, auditSetItemId: string, kopyaNo: number, ad: string) {
  return calistir<DosyaFormu>({
    id: yeniKimlik(), tur: "form.baslat", projectId, zaman: Date.now(), ozet: `${ad}: süreç başlatma`,
    istek: { method: "post", url: `${dosyaYolu(projectId)}/forms/start`, body: { auditSetItemId, kopyaNo } },
  })
}

export async function formuTamamla(projectId: string, auditSetItemId: string, kopyaNo: number, ad: string) {
  return calistir<DosyaFormu>({
    id: yeniKimlik(), tur: "form.tamamla", projectId, zaman: Date.now(), ozet: `${ad}: tamamlama`,
    istek: { method: "post", url: `${dosyaYolu(projectId)}/forms/complete`, body: { auditSetItemId, kopyaNo } },
  })
}

/**
 * Çoğalt: kopya numarası istemcide seçilir (en büyük + 1) ki kuyruktaki
 * sonraki yazmalar o numarayı kullanabilsin; sunucuda doluysa çakışma olur.
 */
export async function formuCogalt(projectId: string, auditSetItemId: string, kaynakKopyaNo: number, mevcutNumaralar: number[], ad: string) {
  const kopyaNo = Math.max(1, ...mevcutNumaralar) + 1
  const sonuc = await calistir<DosyaFormu>({
    id: yeniKimlik(), tur: "form.cogalt", projectId, zaman: Date.now(), ozet: `${ad}: çoğaltma (Kopya ${kopyaNo})`,
    istek: { method: "post", url: `${dosyaYolu(projectId)}/forms/duplicate`, body: { auditSetItemId, kaynakKopyaNo, kopyaNo } },
    ek: { auditSetItemId, kaynakKopyaNo, kopyaNo },
  })
  return { ...sonuc, kopyaNo }
}

/**
 * Genel Bilgiler değerleri (yalnız değişen üst düzey anahtarlar). Bağlantı
 * varken sunucunun yanıtı döner — "Personel" alanının açtığı görevler orada.
 */
export async function genelDegerleriYaz(projectId: string, degerler: Record<string, unknown>) {
  const s = await calistir<Awaited<ReturnType<typeof updateProjectValues>>>(genelKomutu(projectId, degerler))
  return s.kuyrukta ? { kuyrukta: true as const } : { kuyrukta: false as const, sonuc: s.veri }
}

/** Kontrol listesi oturumu — kimliği istemcide üretilir (yanıtlar hemen yazılsın). */
export async function oturumAc(projectId: string, checklistId: string, kullaniciId: string | null) {
  const id = yeniKimlik()
  const s = await calistir<ElevatorChecklistSession>({
    id: yeniKimlik(), tur: "oturum.ac", projectId, zaman: Date.now(), ozet: "Kontrol listesi oturumu açma",
    istek: { method: "post", url: `${dosyaYolu(projectId)}/checklist-entries`, body: { checklistId, id } },
    ek: { sessionId: id, checklistId, kullaniciId },
  })
  return s.kuyrukta ? id : s.veri.id
}

export async function yanitiYaz(
  projectId: string,
  sessionId: string,
  govde: UpsertResponsePayload,
  /** Ölçüm alanının kodu — kuyruğa girerse önbellekteki yanıtta ölçüm görünsün. */
  olcumKodu?: (dataRecordFieldId: string) => string | undefined,
): Promise<YazmaSonucu> {
  const k = yanitKomutu(projectId, sessionId, govde)
  if (olcumKodu) {
    k.ek = { ...k.ek, olcumKodlari: Object.fromEntries((govde.measurements ?? []).map((m) => [m.dataRecordFieldId, olcumKodu(m.dataRecordFieldId)])) }
  }
  return calistir(k)
}

export async function yanitiSil(projectId: string, sessionId: string, checklistItemId: string): Promise<YazmaSonucu> {
  return calistir(yanitSilKomutu(projectId, sessionId, checklistItemId))
}

export async function oturumuTamamla(projectId: string, sessionId: string, ad: string) {
  return calistir<ElevatorChecklistSession>({
    id: yeniKimlik(), tur: "oturum.tamamla", projectId, zaman: Date.now(), ozet: `${ad}: kontrol listesini tamamlama`,
    istek: { method: "post", url: `${dosyaYolu(projectId)}/checklist-entries/${sessionId}/complete`, body: {} },
    ek: { sessionId },
  })
}

/** Harici doküman: bayt cihazda saklanır, bağlantı gelince yüklenir. */
export async function hariciDokumanYukle(projectId: string, auditSetItemId: string, kopyaNo: number, dosya: File) {
  const id = yeniKimlik()
  const blobAnahtari = `dosya:${id}`
  await depoYaz(blobAnahtari, dosya)
  const s = await calistir({
    id, tur: "dosya.yukle", projectId, zaman: Date.now(), ozet: `Harici doküman: ${dosya.name}`,
    dosya: { blobAnahtari, ad: dosya.name, mime: dosya.type || "application/octet-stream", boyut: dosya.size, auditSetItemId, kopyaNo },
  })
  return s
}

// --- Notlar (dosyanın sağ paneli) ---

/** Notlar sekmesinin önbellek anahtarı — `ContentTypeNotesTab` ile aynı. */
export const dosyaNotlariAnahtari = (projectId: string) => ["elevator-project", projectId, "notes"] as const

/** Not kimliği istemcide üretilir: çevrimdışı eklenen not hemen düzenlenip silinebilsin. */
export async function notEkle(projectId: string, govde: string) {
  const id = yeniKimlik()
  const s = await calistir<ContentTypeNote>({
    id: yeniKimlik(), tur: "not.ekle", projectId, zaman: Date.now(), ozet: "Not ekleme",
    istek: { method: "post", url: `${dosyaYolu(projectId)}/notes`, body: { body: govde, id } },
    ek: { notId: id },
  })
  return s
}

export async function notuYaz(projectId: string, notId: string, govde: string): Promise<YazmaSonucu> {
  return calistir({
    id: yeniKimlik(), tur: "not.yaz", projectId, zaman: Date.now(), ozet: "Not düzenleme",
    birlestir: `not:${notId}`,
    istek: { method: "patch", url: `${dosyaYolu(projectId)}/notes/${notId}`, body: { body: govde } },
    ek: { notId },
  })
}

export async function notuSil(projectId: string, notId: string): Promise<YazmaSonucu> {
  return calistir({
    id: yeniKimlik(), tur: "not.sil", projectId, zaman: Date.now(), ozet: "Not silme",
    // Bekleyen düzenleme gereksiz: silme onun yerini alır.
    birlestir: `not:${notId}`,
    istek: { method: "delete", url: `${dosyaYolu(projectId)}/notes/${notId}` },
    ek: { notId },
  })
}

export async function notlariSirala(projectId: string, ids: string[]): Promise<YazmaSonucu> {
  return calistir({
    id: yeniKimlik(), tur: "not.sirala", projectId, zaman: Date.now(), ozet: "Not sıralama",
    birlestir: `not-sira:${projectId}`,
    istek: { method: "patch", url: `${dosyaYolu(projectId)}/notes/reorder`, body: { ids } },
  })
}

// --- Denetim ekibi ---

export type EkipSonucu = Sonuc<DenetimEkibi>

/** Üye kimliği istemcide üretilir: çevrimdışı eklenen kişi pasife alınıp çıkarılabilsin. */
export async function ekipUyesiEkle(
  projectId: string, birimKodu: string, personel: { id: string; code: string; name: string }, birimAdi: string,
): Promise<EkipSonucu> {
  const id = yeniKimlik()
  return calistir({
    id: yeniKimlik(), tur: "ekip.uye.ekle", projectId, zaman: Date.now(), ozet: `Ekip: ${personel.name} → ${birimAdi}`,
    istek: { method: "post", url: `${dosyaYolu(projectId)}/ekip/uyeler`, body: { birimKodu, personnelId: personel.id, id } },
    ek: { uyeId: id, birimKodu, personel },
  })
}

export async function ekipUyesiDurumu(projectId: string, uyeId: string, aktif: boolean, ad: string): Promise<EkipSonucu> {
  return calistir({
    id: yeniKimlik(), tur: "ekip.uye.durum", projectId, zaman: Date.now(), ozet: `Ekip: ${ad} ${aktif ? "aktife" : "pasife"} alma`,
    birlestir: `ekip-durum:${uyeId}`,
    istek: { method: "patch", url: `${dosyaYolu(projectId)}/ekip/uyeler/${uyeId}`, body: { aktif } },
    ek: { uyeId, aktif },
  })
}

export async function ekipUyesiCikar(projectId: string, uyeId: string, ad: string): Promise<EkipSonucu> {
  return calistir({
    id: yeniKimlik(), tur: "ekip.uye.cikar", projectId, zaman: Date.now(), ozet: `Ekip: ${ad} çıkarma`,
    istek: { method: "delete", url: `${dosyaYolu(projectId)}/ekip/uyeler/${uyeId}` },
    ek: { uyeId },
  })
}

/** Dosyaya özel birim — kimliği istemcide: çevrimdışı açılan birime hemen kişi atanabilsin. */
export async function ozelBirimEkle(projectId: string, ad: string): Promise<EkipSonucu> {
  const id = yeniKimlik()
  return calistir({
    id: yeniKimlik(), tur: "ekip.birim.ekle", projectId, zaman: Date.now(), ozet: `Ekip: "${ad}" birimini ekleme`,
    istek: { method: "post", url: `${dosyaYolu(projectId)}/ekip/birimler`, body: { ad, id } },
    ek: { birimId: id, ad },
  })
}

export async function ozelBirimSil(projectId: string, birimId: string, ad: string): Promise<EkipSonucu> {
  return calistir({
    id: yeniKimlik(), tur: "ekip.birim.sil", projectId, zaman: Date.now(), ozet: `Ekip: "${ad}" birimini silme`,
    istek: { method: "delete", url: `${dosyaYolu(projectId)}/ekip/birimler/${birimId}` },
    ek: { birimId },
  })
}

// --- Önbelleğe işleme ----------------------------------------------------------

/** Kuyruğa giren komutu ekranların okuduğu önbelleğe yansıtır. */
export function onbellegeIsle(k: Komut) {
  const qc = qcRef
  if (!qc) return
  const simdi = new Date().toISOString()
  const formlar = ["elevator-project-forms", k.projectId]
  const formuGuncelle = (auditSetItemId: string, kopyaNo: number, f: (x: DosyaFormu) => DosyaFormu, yoksa?: () => DosyaFormu) =>
    qc.setQueryData<DosyaFormu[]>(formlar, (liste) => {
      if (!liste) return liste
      const var_ = liste.some((x) => x.auditSetItemId === auditSetItemId && x.kopyaNo === kopyaNo)
      if (!var_) return yoksa ? [...liste, yoksa()] : liste
      return liste.map((x) => (x.auditSetItemId === auditSetItemId && x.kopyaNo === kopyaNo ? f(x) : x))
    })
  const body = (k.istek?.body ?? {}) as Record<string, unknown>
  switch (k.tur) {
    case "genel.yaz":
      qc.setQueryData<ElevatorProject>(["elevator-project", k.projectId], (p) =>
        p ? { ...p, data: { ...((p.data ?? {}) as Record<string, unknown>), ...(body.values as object) } } : p)
      return
    case "form.yaz": {
      const g = body as Parameters<typeof dosyaFormunuYaz>[1]
      formuGuncelle(g.auditSetItemId, g.kopyaNo, (x) => ({
        ...x,
        ...(g.data !== undefined ? { data: g.data } : {}),
        ...(g.genelBilgilerden !== undefined ? { genelBilgilerden: g.genelBilgilerden } : {}),
      }))
      return
    }
    case "form.baslat": {
      const { auditSetItemId, kopyaNo } = body as { auditSetItemId: string; kopyaNo: number }
      formuGuncelle(auditSetItemId, kopyaNo, (x) => ({ ...x, baslangic: x.baslangic ?? simdi }), () => ({
        id: "", auditSetItemId, kopyaNo, genelBilgilerden: true, data: {}, sema: null, baslangic: simdi, updatedAt: simdi,
      }))
      return
    }
    case "form.tamamla": {
      const { auditSetItemId, kopyaNo } = body as { auditSetItemId: string; kopyaNo: number }
      formuGuncelle(auditSetItemId, kopyaNo, (x) => ({ ...x, tamamlanma: simdi, onayDurumu: "BEKLIYOR" }))
      return
    }
    case "form.cogalt": {
      const { auditSetItemId, kaynakKopyaNo, kopyaNo } = k.ek as { auditSetItemId: string; kaynakKopyaNo: number; kopyaNo: number }
      qc.setQueryData<DosyaFormu[]>(formlar, (liste) => {
        if (!liste || liste.some((x) => x.auditSetItemId === auditSetItemId && x.kopyaNo === kopyaNo)) return liste
        const kaynak = liste.find((x) => x.auditSetItemId === auditSetItemId && x.kopyaNo === kaynakKopyaNo)
        return [...liste, {
          id: `cevrimdisi-${k.id}`, auditSetItemId, kopyaNo, genelBilgilerden: kaynak?.genelBilgilerden ?? true, data: {},
          sema: kaynak?.baslangic ? kaynak.sema : null, baslangic: kaynak?.baslangic ?? null, updatedAt: simdi,
        }]
      })
      return
    }
    case "oturum.ac": {
      const { sessionId, checklistId, kullaniciId } = k.ek as { sessionId: string; checklistId: string; kullaniciId: string | null }
      const oturum: ElevatorChecklistSession = {
        id: sessionId, elevatorProjectId: k.projectId, checklistId, userId: kullaniciId ?? "", status: "IN_PROGRESS",
        startedAt: simdi, completedAt: null, createdAt: simdi, updatedAt: simdi,
      }
      qc.setQueryData<PaginatedSessions>(["elevator-project-sessions", k.projectId], (s) =>
        s && !s.data.some((o) => o.id === sessionId) ? { ...s, data: [...s.data, oturum], total: s.total + 1 } : s)
      if (!qc.getQueryData(["session-detail", k.projectId, sessionId])) {
        qc.setQueryData<SessionDetail>(["session-detail", k.projectId, sessionId], { ...oturum, responses: [] })
      }
      return
    }
    case "oturum.tamamla": {
      const { sessionId } = k.ek as { sessionId: string }
      const bitir = <T extends ElevatorChecklistSession>(o: T): T => ({ ...o, status: "COMPLETED", completedAt: simdi })
      qc.setQueryData<PaginatedSessions>(["elevator-project-sessions", k.projectId], (s) =>
        s ? { ...s, data: s.data.map((o) => (o.id === sessionId ? bitir(o) : o)) } : s)
      qc.setQueryData<SessionDetail>(["session-detail", k.projectId, sessionId], (d) => (d ? bitir(d) : d))
      return
    }
    case "yanit.yaz":
    case "yanit.sil": {
      const { sessionId } = k.ek as { sessionId: string }
      const g = body as unknown as UpsertResponsePayload
      const maddeId = k.tur === "yanit.yaz" ? g.checklistItemId : (k.ek as { checklistItemId: string }).checklistItemId
      const kodlar = (k.ek as { olcumKodlari?: Record<string, string | undefined> }).olcumKodlari ?? {}
      qc.setQueryData<SessionDetail>(["session-detail", k.projectId, sessionId], (d) => {
        if (!d) return d
        const digerleri = d.responses.filter((r) => r.checklistItemId !== maddeId)
        if (k.tur === "yanit.sil") return { ...d, responses: digerleri }
        const eski = d.responses.find((r) => r.checklistItemId === maddeId)
        const yanit: ItemResponse = {
          id: eski?.id ?? `cevrimdisi-${maddeId}`,
          sessionId, checklistItemId: maddeId, result: g.result, notes: g.notes ?? null,
          nonConformities: (g.nonConformities ?? []).map((text, i) => ({ id: `cevrimdisi-${maddeId}-${i}`, text })),
          measurements: g.measurements
            ? g.measurements.flatMap((m) => {
                const code = kodlar[m.dataRecordFieldId]
                return code ? [{ id: `cevrimdisi-${m.dataRecordFieldId}`, value: m.value, dataRecordField: { code, name: code, unit: null } }] : []
              })
            : eski?.measurements ?? [],
          photos: eski?.photos ?? [],
          createdAt: eski?.createdAt ?? simdi,
          updatedAt: simdi,
        }
        return { ...d, responses: [...digerleri, yanit] }
      })
      return
    }
    case "not.ekle": {
      const { notId } = k.ek as { notId: string }
      qc.setQueryData<ContentTypeNote[]>(dosyaNotlariAnahtari(k.projectId), (liste) =>
        liste && !liste.some((n) => n.id === notId)
          // Sunucu da yeni notu sona ekliyor.
          ? [...liste, { id: notId, contentTypeId: "", body: String(body.body ?? ""), sortOrder: liste.length, createdByUserId: null, createdAt: simdi, updatedAt: simdi }]
          : liste)
      return
    }
    case "not.yaz": {
      const { notId } = k.ek as { notId: string }
      qc.setQueryData<ContentTypeNote[]>(dosyaNotlariAnahtari(k.projectId), (liste) =>
        liste?.map((n) => (n.id === notId ? { ...n, body: String(body.body ?? ""), updatedAt: simdi } : n)))
      return
    }
    case "not.sil": {
      const { notId } = k.ek as { notId: string }
      qc.setQueryData<ContentTypeNote[]>(dosyaNotlariAnahtari(k.projectId), (liste) => liste?.filter((n) => n.id !== notId))
      return
    }
    case "not.sirala": {
      const ids = (body.ids ?? []) as string[]
      qc.setQueryData<ContentTypeNote[]>(dosyaNotlariAnahtari(k.projectId), (liste) =>
        liste
          ? [...liste]
              .sort((a, b) => (ids.indexOf(a.id) + 1 || Infinity) - (ids.indexOf(b.id) + 1 || Infinity))
              .map((n, i) => ({ ...n, sortOrder: i }))
          : liste)
      return
    }
    case "ekip.uye.ekle":
    case "ekip.uye.durum":
    case "ekip.uye.cikar":
    case "ekip.birim.ekle":
    case "ekip.birim.sil": {
      const ek = k.ek as {
        uyeId?: string; birimId?: string; ad?: string; aktif?: boolean; birimKodu?: string
        personel?: { id: string; code: string; name: string }
      }
      qc.setQueryData<DenetimEkibi>(denetimEkibiAnahtari(k.projectId), (e) => {
        if (!e) return e
        switch (k.tur) {
          case "ekip.uye.ekle":
            return e.uyeler.some((u) => u.id === ek.uyeId || (u.birimKodu === ek.birimKodu && u.personnel.id === ek.personel!.id))
              ? e
              : { ...e, uyeler: [...e.uyeler, { id: ek.uyeId!, birimKodu: ek.birimKodu!, aktif: true, aktiviteVar: false, personnel: ek.personel! }] }
          case "ekip.uye.durum":
            return { ...e, uyeler: e.uyeler.map((u) => (u.id === ek.uyeId ? { ...u, aktif: !!ek.aktif } : u)) }
          case "ekip.uye.cikar":
            return { ...e, uyeler: e.uyeler.filter((u) => u.id !== ek.uyeId) }
          case "ekip.birim.ekle":
            return e.ozelBirimler.some((b) => b.id === ek.birimId)
              ? e
              : { ...e, ozelBirimler: [...e.ozelBirimler, { id: ek.birimId!, kod: `ozel:${ek.birimId}`, ad: ek.ad! }] }
          default: {
            const kod = `ozel:${ek.birimId}`
            return { ozelBirimler: e.ozelBirimler.filter((b) => b.id !== ek.birimId), uyeler: e.uyeler.filter((u) => u.birimKodu !== kod) }
          }
        }
      })
      return
    }
    case "dosya.yukle": {
      const d = k.dosya!
      qc.setQueryData<HariciDokuman[]>(hariciDokumanAnahtari(k.projectId, d.auditSetItemId, d.kopyaNo), (liste) =>
        liste && !liste.some((x) => x.id === `cevrimdisi-${k.id}`)
          ? [...liste, {
              id: `cevrimdisi-${k.id}`, sortOrder: liste.length, createdAt: simdi, cevrimdisi: true,
              file: { id: "", filename: d.ad, mimeType: d.mime, size: d.boyut },
            }]
          : liste)
      return
    }
  }
}

// --- Gönderme ------------------------------------------------------------------

/**
 * Kuyruğu sırayla gönderir. Ağ hatasında durur (sonra yeniden denenir);
 * sunucunun reddettiği komut (4xx) çakışma listesine geçer — aksi hâlde tek
 * reddedilen komut kuyruğun geri kalanını sonsuza dek bekletirdi. 5xx'te de durulur.
 */
export async function kuyruguGonder(qc: QueryClient | null = qcRef): Promise<void> {
  // Süren bir tur varsa onu bekle, sonra yeni tur: "Şimdi gönder" boşa düşmesin.
  while (suren) await suren
  suren = gonderimTuru(qc).finally(() => { suren = null })
  return suren
}

let suren: Promise<void> | null = null

async function gonderimTuru(qc: QueryClient | null): Promise<void> {
  await yukle()
  if (komutlar.length === 0) return
  gonderiliyor = true
  bildir()
  const dokunulan = new Set<string>()
  try {
    while (komutlar.length) {
      const k = komutlar[0]
      try {
        await gonderBir(k)
        dokunulan.add(k.projectId)
      } catch (e) {
        const durumKodu = axios.isAxiosError(e) ? e.response?.status : undefined
        const kalici = e instanceof KaliciHata || (durumKodu !== undefined && durumKodu >= 400 && durumKodu < 500)
        if (!kalici) break
        reddedilenler = [...reddedilenler, { id: k.id, ozet: k.ozet, hata: hataMetni(e), zaman: Date.now(), projectId: k.projectId }]
        if (k.dosya) await depoSil(k.dosya.blobAnahtari)
        dokunulan.add(k.projectId)
      }
      // Gönderilen ya da reddedilen komut düşer — arada yenisi eklendiyse o kalır.
      komutlar = komutlar.filter((x) => x !== k)
      await sakla()
    }
  } finally {
    gonderiliyor = false
    bildir()
    if (qc) {
      for (const pid of dokunulan) {
        void qc.invalidateQueries({ queryKey: ["elevator-project-forms", pid] })
        void qc.invalidateQueries({ queryKey: ["elevator-project-sessions", pid] })
        void qc.invalidateQueries({ queryKey: ["session-detail", pid] })
        void qc.invalidateQueries({ queryKey: ["elevator-project", pid] })
        void qc.invalidateQueries({ queryKey: ["elevator-project-form-files", pid] })
        void qc.invalidateQueries({ queryKey: denetimEkibiAnahtari(pid) })
        // Genel Bilgiler'deki "Personel" alanı gönderilince görev açılmış olabilir.
        void qc.invalidateQueries({ queryKey: ["personnel-tasks"] })
      }
    }
  }
}

function hataMetni(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const m = (e.response?.data as { message?: unknown } | undefined)?.message
    return Array.isArray(m) ? m.join(" ") : String(m ?? e.message)
  }
  return (e as Error).message
}

/**
 * Uygulama açılışında bir kez: kuyruğu yükler, bağlantı gelince ve sekme
 * öne geldiğinde gönderir.
 */
export function kuyruguBaslat(qc: QueryClient) {
  qcRef = qc
  const dene = () => { if (navigator.onLine !== false) void kuyruguGonder(qc) }
  void yukle().then(() => {
    // Yenilenen sayfada bekleyen komutlar önbelleğe yeniden işlenir.
    for (const k of komutlar) onbellegeIsle(k)
    dene()
  })
  window.addEventListener("online", dene)
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") dene() })
  // Sunucu kapalıyken navigator.onLine yine true olabilir: arada bir dene.
  window.setInterval(() => { if (komutlar.length) dene() }, 30_000)
}
